/**
 * @module selectionModel
 *
 * @description
 * This module provides a function to create a new selection model object.
 *
 * TODO: move the definitions from xstate-machine to here.  Have xstate-machine
 * depend on this module (not the other way around).
 *
 */
import { PURPOSE_TYPES } from '../sum-types';
import { InputError, InvalidStateError } from '../LuciErrors';

export const ALL_STATE = {
  SELECT_ALL: 'SELECT_ALL',
  SELECT_NONE: 'SELECT_NONE',
};
export const REQUEST_TYPES = {
  REQUEST: 'REQUEST',
  ANTIREQUEST: 'ANTIREQUEST',
  UNINITIALIZED: 'UNINITIALIZED',
};
export const COMPUTATION_TYPES = {
  SELECT: 'SELECT',
  REDUCE: 'REDUCE',
  SERIES: 'SERIES',
  EMPTY: 'EMPTY',
};
export const EVENT_TYPES = {
  INIT: 'INIT',
  RESUME: 'RESUME',
  RESET: 'RESET',
  onRowClick: 'onRowClick',
  onToggleAll: 'onToggleAll',
  onSetReduceComputation: 'onSetReduceComputation',
  enableAntiRequestFeature: 'enableAntiRequestFeature',
  disableAntiRequestFeature: 'disableAntiRequestFeature',
};
export { PURPOSE_TYPES };

export const initialSelectionModel = {
  disableAntiRequest: false,
  rowCountTotal: null,
  computationType: COMPUTATION_TYPES.EMPTY,
  requestType: REQUEST_TYPES.UNINITIALIZED,
  values: {},
};

/*
export const schema = {
  events: {} as
    | { type: 'onRowClick'; id: string; isSelected: boolean }
    | { type: 'onToggleAll'; isSelected: boolean },
}; */
//-----------------------------------------------------------------------------
/* eslint-disable no-console */

const ALL = '__ALL__';
const baseRequestValue = (request) => ({ [ALL]: { value: ALL, request } });

// ----------------------------------------------------------------------------
// Helpers for the simplified selection model
// ----------------------------------------------------------------------------
//
// The values dict is in ONE of two shapes at any time:
//
//   1. Default shape:  { __ALL__: { value: '__ALL__', request: bool } }
//      Length is exactly 1. The boolean tells us whether the default state is
//      "all selected" (true → ANTIREQUEST default) or "no values selected"
//      (false → REQUEST default).
//
//   2. Override shape: { key1: {request}, key2: {request}, ... }
//      Length 1..(rowCountTotal-1). The __ALL__ key is NOT present. Each entry
//      represents an explicit toggle from the (implied) default state. The
//      `request` value of every entry mirrors the requestType:
//        - REQUEST mode: every entry has request=true (these are the
//          included values in the whitelist)
//        - ANTIREQUEST mode: every entry has request=false (these are the
//          excluded values in the blacklist)
//
// __ALL__ and explicit entries NEVER coexist.
//
// Mode switching:
//   - If a click in REQUEST mode brings the override count to rowCountTotal,
//     the user has individually selected every row → switch to ANTIREQUEST
//     and reset to default { __ALL__: {request: true} } (all selected).
//   - If a click in ANTIREQUEST mode brings the override count to rowCountTotal,
//     the user has individually deselected every row → switch to REQUEST and
//     reset to default { __ALL__: {request: false} } (no values selected).
//
// disableAntiRequest:
//   - True for time-series components (mspan). Locks the model in REQUEST
//     mode. Mode switching is suppressed.

/** True iff the values dict is in the default shape (only __ALL__). */
const isDefaultShape = (values) => {
  const keys = Object.keys(values);
  return keys.length === 1 && keys[0] === ALL;
};

/** Read the request boolean of the __ALL__ entry, if present. */
const getAllRequest = (values) => values?.[ALL]?.request;

/** Build the default shape for a given mode. */
const defaultValuesForMode = (requestType) =>
  requestType === REQUEST_TYPES.ANTIREQUEST
    ? { [ALL]: { value: ALL, request: true } }
    : { [ALL]: { value: ALL, request: false } };

/** Opposite mode. */
const oppositeMode = (requestType) =>
  requestType === REQUEST_TYPES.REQUEST
    ? REQUEST_TYPES.ANTIREQUEST
    : REQUEST_TYPES.REQUEST;

// ----------------------------------------------------------------------------
// xstate guards (kept for backwards compatibility with the existing machine
// transitions, but the toggle handler now does the work directly so most of
// these become trivial)
// ----------------------------------------------------------------------------
export const isAllRequestValue = ({ selectionModel }) => {
  if (isDefaultShape(selectionModel.values)) {
    return getAllRequest(selectionModel.values);
  }
  // No __ALL__ — derive the implied default from the mode
  return selectionModel.requestType === REQUEST_TYPES.ANTIREQUEST;
};

export const shouldSwitchBuilder = () => false;
export const shouldClearValues = () => false;

// ----------------------------------------------------------------------------

/**
 * The publick interface for creating a new selectionModel. See onEnterAllStateFn
 * for additional internal logic.
 *
 * Create a new selection model object. Default values:
 *  - requestType: REQUEST
 *  - computationType: SELECT for quality, REDUCE for mcomp, SERIES for mspan
 *
 * @param {string} purpose - The purpose of the selection model.
 * @param {number} rowCountTotal - The total number of rows in the grid.
 * @returns {Object} A new selection model object.
 * @throws {InputError} If the purpose is not recognized.
 */
export const newSelectionModel = ({ purpose, rowCountTotal, reduced: reducedProp }) => {
  let computationType;
  let reduced;
  let values;
  let disableAntiRequest = false;
  switch (purpose) {
    case PURPOSE_TYPES.QUALITY:
      computationType = COMPUTATION_TYPES.SELECT;
      reduced = undefined;
      values = baseRequestValue(true);
      break;
    case PURPOSE_TYPES.MCOMP:
      computationType = COMPUTATION_TYPES.REDUCE;
      reduced = reducedProp || true;
      values = baseRequestValue(false);
      break;
    case PURPOSE_TYPES.MSPAN:
      computationType = COMPUTATION_TYPES.SERIES;
      reduced = reducedProp || false;
      values = baseRequestValue(true);
      disableAntiRequest = true;
      break;
    default:
      throw new InputError(`Unexpected purpose value: ${purpose}`);
  }

  return {
    rowCountTotal,
    requestType: values[ALL].request
      ? REQUEST_TYPES.ANTIREQUEST // exclude from select all
      : REQUEST_TYPES.REQUEST, // add to select none
    computationType,
    meta: {
      purpose,
      reduced,
    },
    values,
    disableAntiRequest,
  };
};

/**
 * True if there is exactly one explicit override value (i.e., one entry
 * besides __ALL__). With the new model, that means exactly one key total
 * AND that key is not __ALL__.
 */
export const isSingleValue = (selectionModel = {}) => {
  const keys = Object.keys(selectionModel.values || {});
  return keys.length === 1 && keys[0] !== ALL;
};

/**
 * True if there are no explicit override values — i.e., we're in the default
 * shape ({ __ALL__: ... }) regardless of mode.
 */
const isEmptyRequest = (selectionModel = {}) => {
  return isDefaultShape(selectionModel.values || {});
};
/**
 * Interpret the selection model to determine whether a component is to be
 * included in the collection of select fields.
 *
 * There is only one case where we can *exclude* the field.
 * 1. isEmpty
 * 2. requestType is request
 * 3. computationType is reduce
 *
 * 🦀 Uncertain what it means to have SERIES with no values selected.
 *
 *
 * @param {Object} selectionModel - The selection model object.
 * @returns {boolean} True if the component is to be included.
 */
export const includeCompInSelectFields = (selectionModel) => {
  const exclude =
    selectionModel.computationType === COMPUTATION_TYPES.REDUCE &&
    selectionModel.requestType === REQUEST_TYPES.REQUEST &&
    isEmptyRequest(selectionModel);
  const incomplete =
    selectionModel.computationType === COMPUTATION_TYPES.SERIES &&
    isEmptyRequest(selectionModel);

  return !exclude && !incomplete;
};

/**
 * 📌
 * Transform the selection model to a graphql matrix request.
 *
 * The transform is only mildly different than the selection model. It removes the
 * __ALL__ key and converts some of the enums to bool values.
 *
 * endpoint
 * @function
 * @param {Object} selectionModel - The selection model object.
 * @returns {Object} { request: [txtValues], antiRequest: bool }
 */
export const mkQualOrCompRequest = (selectionModel, maybeSpanValues) => {
  if (maybeSpanValues) {
    return {
      antiRequest: !Object.values(maybeSpanValues)?.[0]?.request ?? false,
      reduced: selectionModel.computationType === COMPUTATION_TYPES.REDUCE,
      request: Object.values(maybeSpanValues).map(({ value }) => value),
    };
  }

  // New model: __ALL__ alone means default state (no explicit overrides);
  // otherwise the keys are the explicit overrides (no __ALL__ mixed in).
  const inDefaultState = isDefaultShape(selectionModel.values);
  const explicitValues = inDefaultState
    ? []
    : Object.values(selectionModel.values).map(({ value }) => value);

  return {
    antiRequest: selectionModel.requestType === REQUEST_TYPES.ANTIREQUEST,
    reduced: selectionModel.computationType === COMPUTATION_TYPES.REDUCE,
    request: explicitValues,
  };
};

/**
 * Luci selectionModel -> grid selectionModel
 *
 * The goal is to build a "deselect" list. This is because we have a default for
 * select all -> list = [].
 *
 * Depends on the keys in the selectionModel = rowToIdFn return value
 *
 * Returns a function that "deselects" rows for display in the grid.
 * Recall, the default is "select all" where we select none in the grid.
 * The grid will display the checkbox as though selected.
 *
 */
export function mkGridSelectionModelFilter(selectionModel, rowToIdFn = (x) => x) {
  if (!selectionModel) {
    return () => [];
  }

  const { values, requestType } = selectionModel;

  // Default shape: only __ALL__
  if (isDefaultShape(values)) {
    const allRequestValue = getAllRequest(values);
    if (allRequestValue) {
      // All selected → no rows in the deselect list
      return () => [];
    }
    // None selected → all rows in the deselect list
    return (rows) => rows.map(rowToIdFn);
  }

  // Override shape: explicit keys, no __ALL__
  const explicitKeys = Object.keys(values);
  const valueSet = new Set(explicitKeys);

  let deselect;
  if (requestType === REQUEST_TYPES.ANTIREQUEST) {
    // Blacklist: explicit values are the deselected ones
    deselect = (id) => valueSet.has(id);
  } else if (requestType === REQUEST_TYPES.REQUEST) {
    // Whitelist: explicit values are the only selected ones
    deselect = (id) => !valueSet.has(id);
  } else {
    throw new InputError(
      `Unexpected selectionModel requestType: ${requestType}`,
      selectionModel,
    );
  }

  return (rows) => rows.map(rowToIdFn).filter(deselect);
}

/**
 * Header toggle handler — explicit "select all" or "deselect all".
 *
 * Hard reset to the default shape for the target mode:
 *   SELECT_ALL  → ANTIREQUEST + { __ALL__: {request: true } }
 *   SELECT_NONE → REQUEST     + { __ALL__: {request: false} }
 *
 * Wipes any explicit overrides — there's no undo expectation per design.
 */
export const handleToggleAllUpdate = (selectionModel, state) => {
  const fragment = modelFromAllState(state);
  return {
    ...selectionModel,
    ...fragment,
  };
};

/**
 * Legacy "clear values" helper — kept for backwards compatibility with the
 * xstate machine config but no longer needed under the new model. The toggle
 * handler always returns a normalized shape, so there's nothing to clear.
 */
export const handleClearValues = (selectionModel) => selectionModel;
/**
 * Legacy add/remove decision — kept exported for the test suite that imports
 * it via testSuite. Under the new model `handleRowClickUpdate` is a pure
 * toggle and doesn't call this function.
 */
const shouldAddOrRemove = (isSelected, requestType) => {
  if (typeof isSelected !== 'boolean' || !REQUEST_TYPES[requestType]) {
    throw new InputError(
      `Invalid isSelected: ${isSelected} or requestType: ${requestType}`,
    );
  }
  switch (true) {
    case requestType === REQUEST_TYPES.REQUEST && isSelected:
      return 'ADD';
    case requestType === REQUEST_TYPES.REQUEST && !isSelected:
      return 'REMOVE';
    case requestType === REQUEST_TYPES.ANTIREQUEST && isSelected:
      return 'REMOVE';
    case requestType === REQUEST_TYPES.ANTIREQUEST && !isSelected:
      return 'ADD';
    default:
      throw new Error('Unreachable - shouldAddOrRemove');
  }
};

/**
 * onRowClick event handler — pure toggle.
 *
 * The click is a TOGGLE on event.id. We do NOT depend on event.isSelected
 * because MUI's apiRef.current.isRowSelected() doesn't reliably reflect user
 * intent (icon inversion + controlled-mode timing).
 *
 * Algorithm:
 *   1. If we're in the default shape ({__ALL__:...}), drop __ALL__ and add the
 *      clicked id with `request` matching the current mode (REQUEST→true,
 *      ANTIREQUEST→false). Result: { eventId: { request } }.
 *   2. Otherwise we're in override shape. If eventId exists, remove it. If
 *      that empties the dict, reset to default for the current mode.
 *      If eventId doesn't exist, add it (mirroring mode).
 *   3. After mutation, if the new override count equals rowCountTotal AND
 *      antiRequest is enabled, switch modes and reset to the new mode's
 *      default.
 *
 * No silent drops, no asymmetric paths.
 */
export const handleRowClickUpdate = (selectionModel, event, state /* , debug */) => {
  if (!state) {
    throw new InputError('Missing state in handleRowClickUpdate');
  }
  if (!event || typeof event.id === 'undefined') {
    // No id → no-op. Matches the legacy behavior of silently ignoring
    // malformed click events.
    return selectionModel;
  }

  const { values, requestType, rowCountTotal, disableAntiRequest } = selectionModel;
  const { id } = event;
  const inDefault = isDefaultShape(values);
  const requestForMode = requestType === REQUEST_TYPES.REQUEST; // true → REQUEST, false → ANTIREQUEST

  let nextValues;

  if (inDefault) {
    // From default → add the first explicit override
    nextValues = {
      [id]: { value: id, request: requestForMode },
    };
  } else if (id in values) {
    // Toggle off — remove the explicit override
    const { [id]: _drop, ...rest } = values;
    nextValues = rest;
  } else {
    // Add another explicit override
    nextValues = {
      ...values,
      [id]: { value: id, request: requestForMode },
    };
  }

  // Normalize: empty → default for current mode
  let nextRequestType = requestType;
  if (Object.keys(nextValues).length === 0) {
    nextValues = defaultValuesForMode(requestType);
  }

  // Mode switch: if explicit overrides cover every row, flip mode and reset
  // (suppressed when anti-request is disabled, e.g., time-series components)
  if (
    !disableAntiRequest &&
    !isDefaultShape(nextValues) &&
    Object.keys(nextValues).length >= rowCountTotal
  ) {
    nextRequestType = oppositeMode(requestType);
    nextValues = defaultValuesForMode(nextRequestType);
  }

  return {
    ...selectionModel,
    requestType: nextRequestType,
    values: nextValues,
  };
};

/**
 * The predicate return true when the model is valid. It throws an InvalidStateError
 * when otherwise.
 *
 * @param {Object} context
 * @param {Object} event
 * @param {Object} meta - { cond: { state, debug } }
 * @return bool
 */
export const isValidModel = ({ selectionModel }, event, { cond: { state, debug } }) => {
  const { valid, errors } = validateSelectionModel(selectionModel, state);
  if (debug) {
    console.debug('🦀 isValidModel', {
      valid,
      errors,
      state,
      selectionModel,
      values: { ...selectionModel.values },
    });
  }
  if (!valid) {
    throw new InvalidStateError('The selection model reached an invalid state.', {
      verbose: debug,
      cause: {
        selectionModel,
        allState: `derived: ${allStateFromAllValue(selectionModel)}`,
        event,
        state,
        errors,
      },
    });
  }
  return valid;
};

/**
 * Implements Rule 6. starting from state
 * Returns the requestType and values required by definition of allState.
 * @private
 * @param {Object} state - { allState }
 * @returns {Object} { requestType, values }
 */
function modelFromAllState(state) {
  let predicate;

  if (typeof state === 'string') {
    predicate = state;
  } else if (state && typeof state.allState === 'string') {
    predicate = state.allState;
  } else {
    throw new InputError(`Invalid state input: ${JSON.stringify(state)}`);
  }

  const isSelectAllState = predicate === ALL_STATE.SELECT_ALL;
  return {
    values: baseRequestValue(isSelectAllState),
    requestType: requestTypeFromAllValue(isSelectAllState),
  };
}
/**
 * Implements Rule 6. starting from ALL
 * Sets the allState using the ALL value.
 * @function
 * @private
 * @param {Object} selectionModel
 * @returns {ALL_STATE.*} The allState state.
 */
function allStateFromAllValue(model) {
  let predicate;
  if (typeof model === 'boolean') {
    predicate = model;
  } else if (isDefaultShape(model.values)) {
    predicate = model.values[ALL].request;
  } else {
    // Override shape — derive from requestType
    predicate = model.requestType === REQUEST_TYPES.ANTIREQUEST;
  }
  return predicate ? ALL_STATE.SELECT_ALL : ALL_STATE.SELECT_NONE;
}
/**
 * Rule 5.
 * Sets the requestType using the ALL value.
 * When the ALL value is true, the requestType is ANTIREQUEST (all except).
 * When the ALL value is false, the requestType is REQUEST (none except).
 * @function
 * @private
 * @param {Object | bool} selectionModel or the ALL.request value.
 * @returns {ALL_STATE.*} The allState state.
 * @returns {REQUEST_TYPE.*}
 */
function requestTypeFromAllValue(model) {
  let predicate;
  if (typeof model === 'boolean') {
    predicate = model;
  } else if (isDefaultShape(model.values)) {
    predicate = model.values[ALL].request;
  } else {
    return model.requestType;
  }
  return predicate ? REQUEST_TYPES.ANTIREQUEST : REQUEST_TYPES.REQUEST;
}
export const testSuite = {
  ALL,
  requestTypeFromAllValue,
  allStateFromAllValue,
  modelFromAllState,
  baseRequestValue,
  shouldAddOrRemove,
  handleToggleAllUpdate,
};
/**
 * Invariants for the selection model. Return an object with the summary status
 * and list of errors.
 * { valid, safeToDispatch, errors }
 *
 * @function
 * @private
 *
 */
function validateSelectionModel(model, state = { allState: undefined }) {
  const errors = [];

  // Rule 1: Validate requestType and computationType
  if (!REQUEST_TYPES[model.requestType]) {
    errors.push('Rule 1. Invalid requestType');
  }
  if (!COMPUTATION_TYPES[model.computationType]) {
    errors.push('Rule 1. Invalid computationType');
  }
  // Rule 2: Validate rowCountTotal is a number greater than zero
  if (typeof model.rowCountTotal !== 'number' || model.rowCountTotal <= 0) {
    errors.push('Rule 2. rowCountTotal must be a number greater than zero');
  }
  // Rule 3: __ALL__ and explicit overrides cannot coexist.
  // The values dict is in EITHER default shape ({__ALL__:...}) XOR override
  // shape ({key1:..., key2:...}).
  const keys = Object.keys(model.values || {});
  const hasAll = keys.includes(ALL);
  if (hasAll && keys.length !== 1) {
    errors.push(
      'Rule 3. __ALL__ must be the only key when present (no mixing with explicit overrides)',
    );
  }
  if (hasAll && typeof model.values[ALL].request !== 'boolean') {
    errors.push('Rule 3. __ALL__ must have a boolean request value');
  }

  // Rule 4: Override count cannot reach rowCountTotal when anti-request is
  // enabled (mode-switch should have triggered first).
  if (
    !model.disableAntiRequest &&
    !hasAll &&
    keys.length >= model.rowCountTotal
  ) {
    errors.push(
      'Rule 4. Override count must be < rowCountTotal (mode switch should have triggered)',
    );
  }

  // Rule 5: every override entry's `request` mirrors the mode
  //   REQUEST mode → entries have request=true
  //   ANTIREQUEST mode → entries have request=false
  if (!hasAll) {
    const expectedRequest = model.requestType === REQUEST_TYPES.REQUEST;
    const mismatched = keys.find((k) => model.values[k].request !== expectedRequest);
    if (mismatched) {
      errors.push(
        `Rule 5. Override entry '${mismatched}' has request=${model.values[mismatched].request} but mode is ${model.requestType}`,
      );
    }
  } else {
    // In default shape, the __ALL__ request bool must align with requestType
    const allReq = model.values[ALL].request;
    const expectedReq = model.requestType === REQUEST_TYPES.ANTIREQUEST;
    if (allReq !== expectedReq) {
      errors.push(
        `Rule 5. __ALL__ request=${allReq} but mode is ${model.requestType} (expected ${expectedReq})`,
      );
    }
  }

  // Rule 6 (legacy allState alignment) — only meaningful in default shape
  const { allState } = state;
  if (allState && hasAll) {
    const allRequestValue = model.values[ALL].request;
    const expected = allState === ALL_STATE.SELECT_ALL;
    if (allRequestValue !== expected) {
      errors.push(
        `Rule 6. __ALL__ request=${allRequestValue} but allState=${allState}`,
      );
    }
  }

  // Return the validation result with all collected errors
  const valid = errors.length === 0;

  return { valid, errors };
}

/**
 * Tests for equality between two Selection Models
 * @param {Object} a - The first selection model.
 * @param {Object} b - The second selection model.
 * @returns {boolean} True if the selection models are equal.
 * @function
 */
export const eqSelectionModels = (a, b) => {
  if (a.requestType !== b.requestType) return false;
  if (a.computationType !== b.computationType) return false;
  if (a.disableAntiRequest !== b.disableAntiRequest) return false;

  const aKeys = Object.keys(a.values);
  const bKeys = Object.keys(b.values);

  if (aKeys.length !== bKeys.length) return false;

  // Compare actual keys — different sets of explicit overrides
  // are different selection models even when the count matches.
  for (const key of aKeys) {
    if (!(key in b.values)) return false;
    if (a.values[key].request !== b.values[key].request) return false;
  }

  return true;
};
// END
